import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from '@/components/ui/sheet';
import { getSettings, updateSettings } from '@/lib/api';
import { getGuestSettings, updateGuestSettings } from '@/lib/guestStorage';
import { toast } from 'sonner';
import * as analytics from '@/lib/analytics';

const getToday = () => new Date().toISOString().split('T')[0];

export const SettingsPanel = ({ open, onOpenChange, onSettingsUpdate, isGuestMode = false }) => {
  const [settings, setSettings] = useState({
    base_balance: '',
    base_date: '',
    min_balance_alert: '',
    max_balance_alert: '',
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      loadSettings();
    }
  }, [open]);

  const loadSettings = async () => {
    try {
      if (isGuestMode) {
        const data = getGuestSettings();
        setSettings({
          base_balance: data.base_balance?.toString() || '0',
          base_date: data.base_date || getToday(),
          min_balance_alert: data.min_balance_alert?.toString() || '',
          max_balance_alert: data.max_balance_alert?.toString() || '',
        });
      } else {
        const res = await getSettings();
        const data = res.data;
        setSettings({
          base_balance: data.base_balance?.toString() || '0',
          base_date: data.base_date || getToday(),
          min_balance_alert: data.min_balance_alert?.toString() || '',
          max_balance_alert: data.max_balance_alert?.toString() || '',
        });
      }
    } catch (err) {
      console.error('Failed to load settings:', err);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const payload = {
        base_balance: parseFloat(settings.base_balance) || 0,
        base_date: settings.base_date || getToday(),
        clear_min_alert: settings.min_balance_alert === '',
        clear_max_alert: settings.max_balance_alert === '',
      };
      
      // Only include alert values if they're not being cleared
      if (settings.min_balance_alert !== '') {
        payload.min_balance_alert = parseFloat(settings.min_balance_alert);
      }
      if (settings.max_balance_alert !== '') {
        payload.max_balance_alert = parseFloat(settings.max_balance_alert);
      }
      
      if (isGuestMode) {
        updateGuestSettings(payload);
      } else {
        await updateSettings(payload);
      }
      
      // Track analytics events
      analytics.trackStartingBalanceSaved(payload.base_balance);
      if (payload.min_balance_alert !== undefined) {
        analytics.trackLowBalanceAlertSet(payload.min_balance_alert);
      }
      if (payload.max_balance_alert !== undefined) {
        analytics.trackHighBalanceAlertSet(payload.max_balance_alert);
      }
      
      toast.success('Settings saved successfully');
      onSettingsUpdate();
      onOpenChange(false);
    } catch (err) {
      console.error('Failed to save settings:', err);
      toast.error('Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const clearMinAlert = () => setSettings({ ...settings, min_balance_alert: '' });
  const clearMaxAlert = () => setSettings({ ...settings, max_balance_alert: '' });
  const setToday = () => setSettings({ ...settings, base_date: getToday() });

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-[400px]" data-testid="settings-panel">
        <SheetHeader>
          <SheetTitle className="font-heading text-xl">Settings</SheetTitle>
          <SheetDescription>
            Configure your balance and alert thresholds.
          </SheetDescription>
        </SheetHeader>

        <div className="py-6 space-y-6">
          {/* Base Balance */}
          <div className="space-y-2">
            <Label htmlFor="base_balance">Known Balance</Label>
            <Input
              id="base_balance"
              data-testid="base-balance-input"
              type="number"
              step="0.01"
              placeholder="0.00"
              value={settings.base_balance}
              onChange={(e) => setSettings({ ...settings, base_balance: e.target.value })}
              className="font-mono"
            />
            <p className="text-xs text-muted-foreground">
              Your actual account balance on the date below
            </p>
          </div>

          {/* Base Date */}
          <div className="space-y-2">
            <Label htmlFor="base_date">Balance As-Of Date</Label>
            <div className="flex gap-2">
              <Input
                id="base_date"
                data-testid="base-date-input"
                type="date"
                value={settings.base_date}
                onChange={(e) => setSettings({ ...settings, base_date: e.target.value })}
                className="flex-1"
              />
              <Button type="button" variant="outline" size="sm" onClick={setToday}>
                Today
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              The forecast will auto-calculate from this date forward
            </p>
          </div>

          {/* Min Balance Alert */}
          <div className="space-y-2">
            <Label htmlFor="min_alert" className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-destructive"></span>
              Low Balance Alert
            </Label>
            <div className="flex gap-2">
              <Input
                id="min_alert"
                data-testid="min-alert-input"
                type="number"
                step="0.01"
                placeholder="e.g., 500"
                value={settings.min_balance_alert}
                onChange={(e) => setSettings({ ...settings, min_balance_alert: e.target.value })}
                className="font-mono flex-1"
              />
              <Button type="button" variant="outline" size="sm" onClick={clearMinAlert}>
                Clear
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Warn when balance drops below this amount
            </p>
          </div>

          {/* Max Balance Alert */}
          <div className="space-y-2">
            <Label htmlFor="max_alert" className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
              High Balance Alert
            </Label>
            <div className="flex gap-2">
              <Input
                id="max_alert"
                data-testid="max-alert-input"
                type="number"
                step="0.01"
                placeholder="e.g., 10000"
                value={settings.max_balance_alert}
                onChange={(e) => setSettings({ ...settings, max_balance_alert: e.target.value })}
                className="font-mono flex-1"
              />
              <Button type="button" variant="outline" size="sm" onClick={clearMaxAlert}>
                Clear
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Highlight when balance exceeds this amount (excess cash available)
            </p>
          </div>
        </div>

        <SheetFooter>
          <Button 
            onClick={handleSave} 
            disabled={loading}
            data-testid="save-settings-btn"
            className="w-full"
          >
            {loading ? 'Saving...' : 'Save Settings'}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};
